<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { examModule } from '@/store/exam/module'
import { statsModule } from '@/store/stats/module'
import type { Study } from '@pocketprep/types'
import UIKit from '@pocketprep/ui-kit'
import { licenseModule } from '@/store/license/module'
import type { TQuestion } from '@/store/types'

type TTrimmedSubject = {
    subject: string
    trimmedSubject: string
}

export type TFilter = {
    questionType: string
    subjects: string[]
    trimmedSubjects: string[]
}

const { Radio, CheckboxOption, Icon } = UIKit

const props = withDefaults(
    defineProps<{
        defaultQuestionType: string
        defaultSubjects: string[]
        quizSubjects?: Study.Class.ExamMetadataJSON['knowledgeAreas'] | null
        questions?: TQuestion[]
        studentOrGroup?: string
    }>(),
    {
        studentOrGroup: 'group',
        quizSubjects: null,
        questions: () => [],
    }
)

const emit = defineEmits<{
    close: []
    filterChange: [obj: TFilter]
}>()

const studentQuestionTypeOptions = [
    { label: 'Answered', value: 'answered' },
    { label: 'Flagged', value: 'flagged' },
    { label: 'Incorrect', value: 'incorrect' },
    { label: 'Correct', value: 'correct' },
]
const groupQuestionTypeOptions = [
    { label: 'All', value: 'all' },
    { label: 'Flagged', value: 'flagged' },
]

const dropdown = ref<HTMLElement | null>(null)
const questionTypeByGroup = ref<{ label: string; value: string }[] | null>(null)
const questionType = ref<{ label: string; value: string } | null>(null)
const subjects = ref<string[]>([])
const subjectsSet = ref(new Set<string>())
const trimmedSubjects = ref<string[]>([])
const trimmedSubjectsSet = ref(new Set<string>())
const showQuestionTypeOptions = ref(true)
const showSubjectOptions = ref(false)

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const subjectOptions = computed(() => {
    const checkboxOptions: TTrimmedSubject[] = []
    const examObjectId = examModule.getters.getCurrentGroupExam()?.examMetadata.objectId
    const quizSubjectsVal = props.quizSubjects

    if (quizSubjectsVal) {
        if (questionType.value?.value) {
            const questionCounts = props.questions.reduce((count, q) => {
                const subject = q.question.subject as Study.Class.SubjectJSON
                const currentCount: number = count[subject.name] || 0
                count[subject.name] = currentCount + 1
                return count
            }, {} as Record<string, number>)

            Object.values(quizSubjectsVal).forEach(s => {
                if (!questionCounts[s.name]) {
                    questionCounts[s.name] = 0
                }
            })
            for (const key in questionCounts) {
                const count: number = questionCounts[key] || 0
                checkboxOptions.push({
                    subject: `${key} (${count})`,
                    trimmedSubject: key,
                })
            }

            return checkboxOptions
        }
    } else if (examObjectId) {
        const examSubjects = examModule.getters.getExamMetadata()[examObjectId]?.knowledgeAreas

        if (props.studentOrGroup === 'group') {
            // group all questions
            if (questionType.value?.value === 'all') {
                Object.values(examSubjects || []).forEach(subject => {
                    checkboxOptions.push({
                        // If no licenses, ensure count is for free questions (specialCount)
                        subject: `${subject.name} (${currentOrgLicenses.value.length
                            ? subject.count
                            : subject.specialCount})`,
                        trimmedSubject: subject.name,
                    })
                })
                return checkboxOptions
            }

            // group flagged
            if (questionType.value?.value === 'flagged') {
                const groupFlaggedQs = statsModule.getters.getCurrentGroupAggStats().questionStats
                    .filter(q => !q.isMockQuestion && q.numStudentsFlagged > 0)
                const subjectCounts = groupFlaggedQs.reduce((count, subject) => {
                    const currentCount: number = count[subject.subject] || 0
                    count[subject.subject] = currentCount + 1
                    return count
                }, {} as Record<string, number>)

                Object.values(examSubjects || []).forEach(s => {
                    if (!subjectCounts[s.name]) {
                        subjectCounts[s.name] = 0
                    }
                })
                for (const key in subjectCounts) {
                    const count: number = subjectCounts[key] || 0
                    checkboxOptions.push({
                        subject: `${key} (${count})`,
                        trimmedSubject: key,
                    })
                }

                return checkboxOptions
            }
        }

        if (props.studentOrGroup === 'student') {
            if (questionType.value?.value) {
                const questionCounts = props.questions.reduce((count, q) => {
                    const subject = q.question.subject as Study.Class.SubjectJSON
                    const currentCount: number = count[subject.name] || 0
                    count[subject.name] = currentCount + 1
                    return count
                }, {} as Record<string, number>)

                Object.values(examSubjects || []).forEach(s => {
                    if (!questionCounts[s.name]) {
                        questionCounts[s.name] = 0
                    }
                })
                for (const key in questionCounts) {
                    const count: number = questionCounts[key] || 0
                    checkboxOptions.push({
                        subject: `${key} (${count})`,
                        trimmedSubject: key,
                    })
                }

                return checkboxOptions
            }
        }
    }

    return checkboxOptions
})

const stringHasSpaces = (val: string) => {
    return val.includes(' ')
}

const emitClose = () => {
    emit('close')
}

const keydownListener = (e: KeyboardEvent) => {
    if (e.code === 'Escape') {
        e.stopPropagation()
        emitClose()
    }
}

const clickListener = (e: MouseEvent) => {
    const targetEl = e.target as HTMLElement
    const dropdownEl = dropdown.value as HTMLElement
    if (!dropdownEl?.contains(targetEl) && !targetEl?.closest('.student-question-review__side-bar-header')
        && !targetEl?.closest('.group-question-review__side-bar-header')
        && document.body.contains(targetEl)) {
        emitClose()
    }
}

const toggleSubjectOption = (subject: string, trimmedSubjectNames: string, value: boolean) => {
    if (value) {
        subjectsSet.value.add(subject)
        trimmedSubjectsSet.value.add(trimmedSubjectNames)
    } else {
        subjectsSet.value.delete(trimmedSubjectNames)
        trimmedSubjectsSet.value.delete(trimmedSubjectNames)
    }
    subjects.value = [ ...subjectsSet.value ]
    trimmedSubjects.value = [ ...trimmedSubjectsSet.value ]
}

onMounted(() => {
    questionTypeByGroup.value = props.studentOrGroup === 'student'
        ? studentQuestionTypeOptions
        : groupQuestionTypeOptions
    questionType.value = questionTypeByGroup.value.find(
        o => o.value === props.defaultQuestionType
    ) || null
    subjects.value = [ ...props.defaultSubjects ]
    subjectsSet.value = new Set(props.defaultSubjects)

    trimmedSubjects.value = [ ...props.defaultSubjects ]
    trimmedSubjectsSet.value = new Set(props.defaultSubjects)
    document.addEventListener('keydown', keydownListener)
    document.addEventListener('click', clickListener)
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', keydownListener)
    document.removeEventListener('click', clickListener)
})

watch([ questionType, subjects ], () => {
    emit('filterChange', {
        questionType: questionType.value?.value || '',
        subjects: subjects.value,
        trimmedSubjects: trimmedSubjects.value,
    })
}, { deep: true })
</script>

<template>
    <div
        ref="dropdown"
        class="filter-dropdown"
    >
        <div
            class="filter-dropdown__label"
            :class="{ 'filter-dropdown__label--active': showQuestionTypeOptions }"
            tabindex="0"
            :aria-label="`${showQuestionTypeOptions ? 'Hide' : 'Show'} question type filters`"
            @click="showQuestionTypeOptions = !showQuestionTypeOptions; showSubjectOptions = false"
            @keypress.enter="showQuestionTypeOptions = !showQuestionTypeOptions; showSubjectOptions = false"
            @mousedown.prevent
        >
            <span>Filter by Question Type</span>
            <Icon
                type="accordionArrow"
                class="filter-dropdown__arrow"
            />
        </div>
        <Radio
            v-if="showQuestionTypeOptions && !!questionTypeByGroup"
            v-model="questionType"
            aria-live="polite"
            :data="questionTypeByGroup"
            class="filter-dropdown__options"
        />
        <div
            class="filter-dropdown__label"
            :class="{ 'filter-dropdown__label--active': showSubjectOptions }"
            tabindex="0"
            :aria-label="`${showSubjectOptions ? 'Hide' : 'Show'} subject filters`"
            @click="showSubjectOptions = !showSubjectOptions; showQuestionTypeOptions = false"
            @keypress.enter="showSubjectOptions = !showSubjectOptions; showQuestionTypeOptions = false"
            @mousedown.prevent
        >
            <span>Filter by Subject</span>
            <Icon
                type="accordionArrow"
                class="filter-dropdown__arrow"
            />
        </div>
        <div
            v-if="showSubjectOptions"
            aria-live="polite"
            class="filter-dropdown__subject-options"
        >
            <CheckboxOption
                v-for="subjectOption in subjectOptions"
                :key="subjectOption.subject"
                :label="subjectOption.subject"
                class="filter-dropdown__subject-option"
                :class="{
                    'filter-dropdown__subject-option--force-break': !stringHasSpaces(subjectOption.subject)
                }"
                :modelValue="trimmedSubjects.includes(subjectOption.trimmedSubject)"
                @update:modelValue="toggleSubjectOption(subjectOption.subject, subjectOption.trimmedSubject, $event)"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.filter-dropdown {
    background: $white;
    border-radius: 8px;
    box-shadow: 0 6px 22px 0 rgba($brand-black, 0.1);
    padding: 9px 20px;
    box-sizing: border-box;
    width: 100%;
    font-weight: 500;
    max-height: 305px;
    overflow: auto;

    &__label {
        font-size: 10px;
        letter-spacing: 0.2px;
        line-height: 30px;
        font-weight: 600;
        cursor: pointer;
        color: $steel;
        display: flex;
        align-items: center;
        text-transform: uppercase;
        outline: none;
        position: relative;

        &--active svg {
            transform: rotate(180deg);
        }

        &:focus {
            &::before {
                content: '';
                left: -6px;
                top: -2px;
                width: calc(100% + 12px);
                height: 100%;
                border: 1px solid $brand-blue;
                position: absolute;
                border-radius: 6px;
            }
        }

        span {
            margin-right: 6px;
        }

        svg {
            color: $brand-blue;
            width: 7px;
        }
    }

    &__options {
        margin-bottom: 6px;
    }

    &__subject-option {
        &--force-break {
            word-break: break-all;
        }
    }
}
</style>
